/**
 * Utility functions for URL handling and command parameter preservation
 */

import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { CommandType } from '~/protos/agents'

/**
 * Hook to get the current command parameter from the URL
 * @returns The command parameter value or null if not present
 */
export const useCurrentCommandParam = (): string | null => {
  const [searchParams] = useSearchParams()
  return searchParams.get('command')
}

/**
 * Hook to update the current URL with a command parameter
 * @returns Function to update URL with command
 */
export const useUpdateUrlWithCommand = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  return (commandType: CommandType) => {
    const commandName = getCommandUrlName(commandType)
    if (commandName) {
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.set('command', commandName)
      setSearchParams(newSearchParams)
    }
  }
}

/**
 * Hook to remove the command parameter from the current URL
 * @returns Function to remove command from URL
 */
export const useRemoveCommandFromUrl = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  return () => {
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.delete('command')
    newSearchParams.delete('withExamplePrefilled')
    setSearchParams(newSearchParams)

    // Dispatch a custom event to notify the App component to reset conversation state
    window.dispatchEvent(new CustomEvent('commandRemoved'))
  }
}

/**
 * Hook to navigate to a conversation while preserving command parameters
 * @returns Function to navigate to conversation with command preservation
 */
export const useNavigateToConversation = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  return (uuid: string) => {
    const commandParam = searchParams.get('command')
    const newUrl = `/c/${uuid}`
    if (commandParam) {
      navigate(`${newUrl}?command=${commandParam}`)
    } else {
      navigate(newUrl)
    }
  }
}

/**
 * Hook to navigate to a conversation without any command parameters (clean URL)
 * @returns Function to navigate to conversation home
 */
export const useNavigateToConversationHome = () => {
  const navigate = useNavigate()
  const { workspaceUuid } = useParams<{ workspaceUuid: string }>()

  return (uuid: string) => {
    const conversationUrl = `/w/${workspaceUuid}/c/${uuid}`
    navigate(conversationUrl)
  }
}

/**
 * Legacy utility functions for backward compatibility
 * These should be replaced with the hooks above in new code
 */

/**
 * Constructs a conversation URL while preserving the current command parameter
 * @param uuid - The conversation UUID
 * @returns URL string with command parameter preserved if it exists
 */
export const buildConversationUrl = (uuid: string): string => {
  const currentUrl = new URL(window.location.href)
  const commandParam = currentUrl.searchParams.get('command')
  const newUrl = `/c/${uuid}`
  return commandParam ? `${newUrl}?command=${commandParam}` : newUrl
}

/**
 * Navigates to a conversation URL while preserving the command parameter
 * @param uuid - The conversation UUID
 */
export const navigateToConversation = (uuid: string): void => {
  const url = buildConversationUrl(uuid)
  history.pushState({}, '', url)
}

/**
 * Navigates to a conversation URL without any command parameters (clean URL)
 * @param uuid - The conversation UUID
 */
export const navigateToConversationHome = (uuid: string): void => {
  const cleanUrl = `/c/${uuid}`
  history.pushState({}, '', cleanUrl)
}

/**
 * Gets the current command parameter from the URL
 * @returns The command parameter value or null if not present
 */
export const getCurrentCommandParam = (): string | null => {
  const currentUrl = new URL(window.location.href)
  return currentUrl.searchParams.get('command')
}

/**
 * Updates the current URL with a command parameter
 * @param commandType - The CommandType to add to the URL
 */
export const updateUrlWithCommand = (commandType: CommandType): void => {
  const currentUrl = new URL(window.location.href)
  const commandName = getCommandUrlName(commandType)
  if (commandName) {
    currentUrl.searchParams.set('command', commandName)
    history.replaceState({}, '', currentUrl.toString())
  }
}

/**
 * Removes the command parameter from the current URL and triggers state update
 */
export const removeCommandFromUrl = (): void => {
  const currentUrl = new URL(window.location.href)
  currentUrl.searchParams.delete('command')
  history.replaceState({}, '', currentUrl.toString())

  // Dispatch a custom event to notify the App component to reset conversation state
  window.dispatchEvent(new CustomEvent('commandRemoved'))
}

/**
 * Maps CommandType to URL-friendly command names
 */
const commandUrlMap: Partial<Record<CommandType, string>> = {
  [CommandType.NONE]: 'none',
  [CommandType.QUESTION_ANSWERING]: 'question-answering',
  [CommandType.GENERATE_IMAGE]: 'generate-image',
  [CommandType.GENERATE_SIMPLE_CODE_PR]: 'generate-simple-code-pr',
  [CommandType.FIX_PR]: 'fix-pr',
  [CommandType.GENERATE_DOCUMENTATION]: 'generate-documentation',
  [CommandType.GENERATE_FULL_REPO_TECH_MANIFEST_DOCUMENTATION]:
    'generate-full-repo-tech-manifest-documentation',
  [CommandType.AUTODESK_MULE_TO_NODE_JS_MIGRATION]: 'autodesk-mule-to-node-js-migration',
  [CommandType.GENERATE_MAPPING_TABLE]: 'generate-mapping-table',
  [CommandType.GENERATE_MULTI_REPO_FLOW_DIAGRAM]: 'generate-multi-repo-flow-diagram',
  [CommandType.GENERATE_ADD_FLOW_CODE_PR]: 'generate-add-flow-code-pr',
  [CommandType.DATAWEAVE_TO_DATA_CREATION]: 'dataweave-to-data-creation',
  [CommandType.INTEGRATION_TEST]: 'integration-test',
  [CommandType.MUNIT_TEST_CREATION]: 'munit-test-creation',
  [CommandType.GENERATE_DATAWEAVE]: 'generate-dataweave',
  [CommandType.MULTI_REPO_QA]: 'multi-repo-qa',
  [CommandType.GENERAL_MULTI_REPO_QA_DEBUG_AGENT]: 'general-multi-repo-qa-debug-agent',
  [CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION]: 'input-output-example-to-dwl-generation',
  [CommandType.MIGRATION_DOCUMENT]: 'migration-document',
  [CommandType.GENERATE_REPO_ADD_FLOW_CODE_PR]: 'generate-repo-add-flow-code-pr',
  [CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE]:
    'input-output-example-to-dwl-generation-repo-aware',
  [CommandType.GENERATE_DATAWEAVE_REPO_AWARE]: 'generate-dataweave-repo-aware',
  [CommandType.DATAWEAVE_TO_DATA_CREATION_REPO_AWARE]: 'dataweave-to-data-creation-repo-aware',
  [CommandType.CODE_REVIEW]: 'code-review',
  [CommandType.MULESOFT_TO_CAMEL_MIGRATION]: 'mulesoft-to-camel-migration',
  [CommandType.MIGRATION_QUESTION_ANSWERING]: 'migration-question-answering',
  [CommandType.MULE_3_TO_MULE_4_MIGRATION]: 'mule-3-to-mule-4-migration',
  [CommandType.BILLABLE_FLOW_DOCUMENTATION]: 'billable-flow-documentation',
  [CommandType.MIGRATION_ACCELERATOR]: 'migration-accelerator',
  [CommandType.MEGA_QA_AGENT_V1]: 'mega-qa-agent-v1',
  [CommandType.CODE_REVIEW_API]: 'code-review-api',
  [CommandType.MULE_REFACTORING]: 'mule-refactoring',
  [CommandType.SPRING_INTEGRATION_REFACTORING]: 'spring-integration-refactoring',
  [CommandType.MULE_VERSION_UPGRADE]: 'mule-version-upgrade',
  [CommandType.NON_MULE_DOCUMENTATION]: 'non-mule-documentation',
  [CommandType.AZURE_DEPLOY_TEST]: 'azure-deploy-test',
  [CommandType.UNRECOGNIZED]: 'unrecognized'
}

/**
 * Maps URL-friendly command names back to CommandType
 */
const reverseCommandUrlMap: Record<string, CommandType> = Object.entries(commandUrlMap).reduce(
  (acc, [key, value]) => {
    acc[value] = Number(key) as CommandType
    return acc
  },
  {} as Record<string, CommandType>
)

/**
 * Gets the URL-friendly name for a CommandType
 */
export const getCommandUrlName = (commandType: CommandType): string | null => {
  return commandUrlMap[commandType] || null
}

/**
 * Gets the CommandType from a URL-friendly name
 */
export const getCommandTypeFromUrlName = (urlName: string): CommandType | null => {
  return reverseCommandUrlMap[urlName] || null
}
